import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type ServiceFormSchema, serviceFormSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { ServiceForm } from "@/forms/service-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditServiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	serviceId: Nullable<string>;
	onComplete: () => Promise<void>;
}

export function EditServiceModal({
	isOpen,
	onClose,
	serviceId,
	onComplete,
}: EditServiceModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const fileUploadController = useFileUpload({ uploadFn });
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		if (!serviceId) return {} as ServiceFormSchema;
		const response = await client.organizations.services[":serviceId"].$get({
			param: { serviceId },
		});
		const json = await responseHandler(response);
		return json.service as ServiceFormSchema;
	});
	const methods = useForm({
		resolver: resolver(serviceFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ServiceFormSchema) {
		try {
			const uploadResults = await fileUploadController.handleOnFileUpload();
			if (!serviceId) throw new Error("Service ID is required");
			await client.organizations.services[":serviceId"].$patch({
				param: { serviceId },
				json: {
					...json,
					uploadResults,
				},
			});
			await onComplete();
			onClose();
		} catch (error) {
			console.error("Failed to update service:", error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Guard */
	if (!serviceId) return null;

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("organization.data.edit_service")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, (e) => {
					console.error("Form errors:", e);
				})}
				width="60rem"
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ServiceForm fileUploadController={fileUploadController} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
